/* CrossDivisionPathsWidget — admin Overview tile.
 *
 * Surfaces which source→target product paths visitors are actually
 * clicking through on the cross-division "Complete the experience" rail.
 * Use it to see which pairings are merchandising well (e.g. iPhone PDP →
 * Cybersecurity Support) and which are getting ignored.
 *
 * Renders:
 *  - Stats strip (clicks, unique paths, unique visitors)
 *  - Division-pair heatmap-lite (e.g. services → systems · 47 clicks)
 *  - Top 10 specific source→target rows with mini bars
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Sparkles, RefreshCw, Cpu, ShoppingBag, GraduationCap, ArrowRight, MousePointerClick, Users, Route,
} from 'lucide-react';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

const KIND_META = {
  systems_service:  { label: 'Systems',  Icon: Cpu,            accent: '#5b8def' },
  services_product: { label: 'Services', Icon: ShoppingBag,    accent: '#c9a227' },
  academy_program:  { label: 'Academy',  Icon: GraduationCap,  accent: '#a875ff' },
};
const KIND_URL = {
  systems_service:  (slug) => `/systems/solutions/${slug}`,
  services_product: (slug) => `/services/p/${slug}`,
  academy_program:  (slug) => `/academy/programs/${slug}`,
};

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function CrossDivisionPathsWidget() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState({ totals: { total_clicks: 0, unique_paths: 0, unique_visitors: 0 }, pairs: [], rows: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/recommendations/top-paths?days=${days}&limit=10`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setData({
          totals: d.totals || { total_clicks: 0, unique_paths: 0, unique_visitors: 0 },
          pairs: d.pairs || [],
          rows: d.rows || [],
        });
      }
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const maxRowCount = Math.max(1, ...data.rows.map((r) => r.count || 0));
  const maxPairCount = Math.max(1, ...data.pairs.map((p) => p.count || 0));
  const empty = !loading && data.totals.total_clicks === 0;

  return (
    <section
      data-testid="admin-cross-division-paths-widget"
      className="rounded-2xl border border-gray-200 bg-white overflow-hidden"
    >
      <header className="px-6 py-4 flex items-center justify-between bg-[#f8f8fa] border-b border-gray-100 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#c9a227]" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#a8871a]">Cross-Division Paths</div>
            <h3 className="text-[15px] font-bold text-gray-900">Where the rail is converting</h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              data-testid={`cross-division-paths-period-${d}`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                days === d ? 'bg-[#c9a227] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#c9a227]'
              }`}
            >
              {d}d
            </button>
          ))}
          <button
            type="button"
            onClick={load}
            data-testid="cross-division-paths-refresh"
            disabled={loading}
            className="p-1.5 rounded-full border border-gray-200 text-gray-500 hover:text-[#c9a227] hover:border-[#c9a227] transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </header>

      <div className="px-6 py-3 grid grid-cols-3 gap-3 border-b border-gray-100">
        <Stat icon={MousePointerClick} label="Rail clicks" value={data.totals.total_clicks} />
        <Stat icon={Route} label="Unique paths" value={data.totals.unique_paths} />
        <Stat icon={Users} label="Unique visitors" value={data.totals.unique_visitors} />
      </div>

      {empty ? (
        <div className="text-center py-10 px-6" data-testid="cross-division-paths-empty">
          <Sparkles className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-[13px] text-gray-500">No rail clicks yet in this window.</p>
          <p className="text-[11.5px] text-gray-400 mt-1">As visitors click between divisions on PDP rails, those paths land here.</p>
        </div>
      ) : (
        <>
          {/* Division-pair breakdown */}
          {data.pairs.length > 0 && (
            <div className="px-6 py-4 border-b border-gray-100" data-testid="cross-division-paths-pairs">
              <div className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-gray-500 mb-2.5">Division pairs</div>
              <div className="flex flex-col gap-1.5">
                {data.pairs.slice(0, 6).map((p) => {
                  const sm = KIND_META[p.source_kind];
                  const tm = KIND_META[p.target_kind];
                  if (!sm || !tm) return null;
                  return (
                    <div key={`${p.source_kind}-${p.target_kind}`} className="flex items-center gap-3 text-[12px]">
                      <span className="inline-flex items-center gap-1.5 w-[110px] shrink-0">
                        <sm.Icon className="w-3 h-3" style={{ color: sm.accent }} />
                        <span className="font-semibold text-gray-700">{sm.label}</span>
                      </span>
                      <ArrowRight className="w-3 h-3 text-gray-300 shrink-0" />
                      <span className="inline-flex items-center gap-1.5 w-[110px] shrink-0">
                        <tm.Icon className="w-3 h-3" style={{ color: tm.accent }} />
                        <span className="font-semibold text-gray-700">{tm.label}</span>
                      </span>
                      <span className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <span className="block h-full rounded-full" style={{ width: `${(p.count / maxPairCount) * 100}%`, background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }} />
                      </span>
                      <span className="w-10 text-right font-bold text-gray-900">{p.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top specific paths */}
          <div className="px-6 py-4" data-testid="cross-division-paths-list">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-gray-500 mb-2.5">Top product paths</div>
            <ul className="flex flex-col gap-2">
              {data.rows.map((r, i) => {
                const sm = KIND_META[r.source_kind];
                const tm = KIND_META[r.target_kind];
                if (!sm || !tm) return null;
                const sUrl = KIND_URL[r.source_kind](r.source_slug);
                const tUrl = KIND_URL[r.target_kind](r.target_slug);
                return (
                  <li key={`${r.source_slug}->${r.target_slug}`} className="flex items-center gap-3 py-1.5 px-2 rounded-md hover:bg-gray-50 transition-colors">
                    <span className="text-[10.5px] font-bold text-gray-400 w-5 text-right">{i + 1}</span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-2 text-[13px]">
                        <a
                          href={sUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={r.source_slug}
                          className="inline-flex items-center gap-1 text-gray-900 hover:text-[#c9a227] truncate max-w-[200px]"
                        >
                          <sm.Icon className="w-3 h-3 shrink-0" style={{ color: sm.accent }} />
                          <span className="truncate font-medium">{r.source_name || r.source_slug}</span>
                        </a>
                        <ArrowRight className="w-3 h-3 text-gray-300 shrink-0" />
                        <a
                          href={tUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={r.target_slug}
                          className="inline-flex items-center gap-1 text-gray-900 hover:text-[#c9a227] truncate max-w-[200px]"
                        >
                          <tm.Icon className="w-3 h-3 shrink-0" style={{ color: tm.accent }} />
                          <span className="truncate font-medium">{r.target_name || r.target_slug}</span>
                        </a>
                      </span>
                      <span className="block mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <span className="block h-full rounded-full" style={{ width: `${(r.count / maxRowCount) * 100}%`, background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }} />
                      </span>
                      <span className="text-[11px] text-gray-500 mt-1 inline-flex items-center gap-2">
                        <span><MousePointerClick className="w-3 h-3 inline mr-1" />{r.count} click{r.count === 1 ? '' : 's'}</span>
                        <span>· {r.unique_visitors} visitor{r.unique_visitors === 1 ? '' : 's'}</span>
                        <span>· {timeAgo(r.last_ts)}</span>
                      </span>
                    </span>
                  </li>
                );
              })}
              {data.rows.length === 0 && (
                <li className="text-center py-4 text-[12px] text-gray-400">No path data in the last {days} days.</li>
              )}
            </ul>
          </div>
        </>
      )}
    </section>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="h-8 w-8 rounded-md bg-[#c9a227]/10 grid place-items-center shrink-0">
        <Icon className="w-3.5 h-3.5 text-[#c9a227]" />
      </span>
      <span className="flex flex-col leading-tight">
        <span className="text-[16px] font-bold text-gray-900">{value}</span>
        <span className="text-[10.5px] uppercase tracking-wider text-gray-500">{label}</span>
      </span>
    </div>
  );
}
